import React, { useEffect, useRef } from 'react';
import Map, { MapRef, Layer, Source, Marker } from 'react-map-gl';
import { MapPin } from 'react-feather';
import './RadiusPreviewMap.css';

interface RadiusPreviewMapProps {
  center: { lat: number; lng: number } | null;
  radiusKm: number;
  isLoading?: boolean;
}

// Generate a circle polygon for the radius
const generateCirclePolygon = (
  centerLng: number,
  centerLat: number,
  radiusKm: number,
  points: number = 64
): number[][] => {
  const coordinates: number[][] = [];
  const earthRadius = 6371; // km

  for (let i = 0; i <= points; i++) {
    const angle = (i / points) * 2 * Math.PI;
    
    // Calculate the lat/lng offset
    const latOffset = (radiusKm / earthRadius) * (180 / Math.PI) * Math.sin(angle);
    const lngOffset = (radiusKm / earthRadius) * (180 / Math.PI) * Math.cos(angle) / 
      Math.cos(centerLat * Math.PI / 180);
    
    coordinates.push([centerLng + lngOffset, centerLat + latOffset]);
  }

  return coordinates;
};

export const RadiusPreviewMap: React.FC<RadiusPreviewMapProps> = ({
  center,
  radiusKm,
  isLoading = false
}) => {
  const mapRef = useRef<MapRef>(null);

  const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN || '';

  // Calculate appropriate zoom level based on radius
  const getZoomForRadius = (radiusKm: number): number => {
    // Rough calculation: zoom level decreases as radius increases
    if (radiusKm <= 1) return 14;
    if (radiusKm <= 2) return 13;
    if (radiusKm <= 5) return 12;
    if (radiusKm <= 10) return 11;
    if (radiusKm <= 20) return 10;
    if (radiusKm <= 50) return 9;
    return 8;
  };

  // Fly to new center when it changes
  useEffect(() => {
    if (center && mapRef.current) {
      mapRef.current.flyTo({
        center: [center.lng, center.lat],
        zoom: getZoomForRadius(radiusKm),
        duration: 1000
      });
    }
  }, [center, radiusKm]);

  // Generate circle GeoJSON
  const circleGeoJSON = center ? {
    type: 'FeatureCollection' as const,
    features: [{
      type: 'Feature' as const,
      properties: {},
      geometry: {
        type: 'Polygon' as const,
        coordinates: [generateCirclePolygon(center.lng, center.lat, radiusKm)]
      }
    }]
  } : null;

  // Show placeholder when no center
  if (!center) {
    return (
      <div className="radius-preview-map">
        <div className="radius-preview-placeholder">
          <MapPin size={32} />
          <span>Enter an address or use current location to preview your zone</span>
        </div>
      </div>
    );
  }

  return (
    <div className="radius-preview-map">
      {isLoading && (
        <div className="radius-preview-loading">
          <div className="loading-spinner"></div>
        </div>
      )}
      <Map
        ref={mapRef}
        initialViewState={{
          longitude: center.lng,
          latitude: center.lat,
          zoom: getZoomForRadius(radiusKm)
        }}
        style={{ width: '100%', height: '100%' }}
        mapStyle="mapbox://styles/mapbox/streets-v11"
        mapboxAccessToken={MAPBOX_TOKEN}
        interactive={false}
      >
        {/* Radius circle fill */}
        {circleGeoJSON && (
          <Source id="radius-circle" type="geojson" data={circleGeoJSON}>
            <Layer
              id="radius-fill"
              type="fill"
              paint={{
                'fill-color': '#3b82f6',
                'fill-opacity': 0.15
              }}
            />
            <Layer
              id="radius-stroke"
              type="line"
              paint={{
                'line-color': '#3b82f6',
                'line-width': 2,
                'line-opacity': 0.8
              }}
            />
          </Source>
        )}

        {/* Center marker */}
        <Marker longitude={center.lng} latitude={center.lat} anchor="center">
          <div className="radius-center-marker">
            <MapPin size={24} />
          </div>
        </Marker>
      </Map>
    </div>
  );
};


